import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';

/**
 * The palette a tag chip is coloured from.
 *
 * Six hues, picked by hashing the tag, so "VIP" is the same colour on every
 * row and on every page without anybody choosing one. That is the whole point:
 * a tag is an audience, and telling two of them apart at a glance is what the
 * colour is for. Each entry names a token so both themes are handled by the
 * token, not by a second palette here.
 */
const HUES = ['violet', 'blue', 'green', 'amber', 'rose', 'teal'] as const;

function hueFor(tag: string): (typeof HUES)[number] {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = (h * 31 + tag.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length];
}

/** One tag, as a pill. */
export function ContactTag({ tag }: { tag: string }) {
  return <span className={`rr-tagchip rr-tagchip--${hueFor(tag)}`}>{tag}</span>;
}

/**
 * The tags on a row: three, then "+N" with the rest behind a tooltip.
 *
 * §4 item 18 of the review: the cell showed three tags and then said nothing,
 * so a contact with six looked identical to one with three.
 */
export function ContactTags({ tags }: { tags: string[] }) {
  const t = useT();
  const shown = tags.slice(0, 3);
  const rest = tags.length - shown.length;
  return (
    <span className="rr-tagchips">
      {shown.map((tag) => <ContactTag key={tag} tag={tag} />)}
      {rest > 0 && (
        <Tooltip label={tags.slice(3).join(', ')} position="top">
          <span className="rr-tagchip rr-tagchip--more">+{rest}</span>
        </Tooltip>
      )}
      {tags.length === 0 && <span className="rr-tagchips__none">{t('common.none')}</span>}
    </span>
  );
}

/**
 * Whether this person may still be messaged.
 *
 * §4 item 17: the badge was colour alone. It is a word, an icon and a tint
 * now, so it survives a greyscale screen and a colour-blind reader.
 */
export function OptInPill({ optedIn }: { optedIn: boolean }) {
  const t = useT();
  return (
    <span className={`rr-optin rr-optin--${optedIn ? 'in' : 'out'}`}>
      {optedIn ? <TickIcon /> : <BlockIcon />}
      {optedIn ? t('contacts.optIn.in') : t('contacts.optIn.out')}
    </span>
  );
}

/** SMS consent in three states: agreed, opted out, or never recorded. */
export type SmsConsent = 'yes' | 'no' | 'unset';

export function smsConsentOf(v: boolean | null | undefined): SmsConsent {
  return v === true ? 'yes' : v === false ? 'no' : 'unset';
}

/**
 * Whether this person may get SMS. Separate from the WhatsApp pill on
 * purpose: consent to one channel is not consent to the other, and "not
 * recorded" is shown as exactly that rather than as a no.
 */
export function SmsConsentPill({ consent }: { consent: SmsConsent }) {
  return <ChannelConsentPill channel="sms" consent={consent} />;
}

/**
 * Email consent (4 October 2026): the same three states as SMS, and just as
 * separate from WhatsApp consent.
 */
export type EmailConsent = SmsConsent;
export const emailConsentOf = smsConsentOf;

export function EmailConsentPill({ consent }: { consent: EmailConsent }) {
  return <ChannelConsentPill channel="email" consent={consent} />;
}

function ChannelConsentPill({ channel, consent }: { channel: 'sms' | 'email'; consent: SmsConsent }) {
  const t = useT();
  const tone = consent === 'yes' ? 'in' : consent === 'no' ? 'out' : 'unset';
  return (
    <span className={`rr-optin rr-optin--${tone}`}>
      {consent === 'yes' ? <TickIcon /> : consent === 'no' ? <BlockIcon /> : <UnsetIcon />}
      {t(`contacts.${channel}.pill.${consent}`)}
    </span>
  );
}

function TickIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3,8.5 6.4,12 13,4.5" />
    </svg>
  );
}
function BlockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" aria-hidden>
      <circle cx="8" cy="8" r="5.6" />
      <path d="M5 8h6" strokeLinecap="round" />
    </svg>
  );
}
function UnsetIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeDasharray="2.4 2.2" aria-hidden>
      <circle cx="8" cy="8" r="5.6" />
    </svg>
  );
}
